import { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { useSelector } from "react-redux";
import { useMutation } from "react-query";
import axios from "axios";
import { toast } from "react-toastify";
import Table from "../../Components/Settings/Table";
import {
  BoxHead,
  BoxContainer,
  BoxTitle,
  Underline,
} from "../../Components/Styles/PageStyles";
import RentingStayRulesModal from "../../Components/Settings/RentingStayRulesModal";
import ManagementDetailsModal from "../../Components/Settings/ManagementDetailsModal";
import NotificationSettings from "../../Components/Settings/NotificationSettings";
import OnboardingSettingsModal from "../../Components/Settings/OnboardingSettings";
import EvictionSettingsModal from "../../Components/Settings/EvictionSettingsModal";
import Logout from "../../Components/Logout";
import Config from "../../Config";
import SideModal from "../../Components/SideModal";

const TableWrapper = tw.h1`my-10 rounded-md`;

const Settings = () => {
  const user = useSelector((state) => state.UserReducer.user);
  const property = useSelector(
    (state) => state.SelectPropertyReducer.selectProperty
  );
  const propertyList = useSelector((state) => state.PropertyReducer.property);
  const [openModal, setOpenModal] = useState(null);
  const [isLogout, setIsLogout] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const [modalStates, setModalStates] = useState({
    managementModel: false,
    rentModel: false,
    notiModel: false,
    onboardingModel: false,
    evictionModel: false,
  });

  const getAllPropertyIds = () => {
    if (!propertyList || propertyList.length === 0) return "";

    const validProperties = propertyList.filter((prop) => prop.id !== 0);
    return validProperties.map((prop) => prop.id).join(",");
  };

  const getPropertyParam = (endpoint = "", selectedPropertyId = null) => {
    if (selectedPropertyId) {
      return selectedPropertyId;
    }
    if (property?.id === 0 && endpoint === "/property/web/renting/rules/") {
      return propertyList[1]?.id || "";
    }
    if (property?.id === 0) {
      return getAllPropertyIds();
    }
    return property?.id;
  };

  const updateModalState = (modalKey, value) => {
    setModalStates((prev) => ({
      ...prev,
      [modalKey]: value,
    }));
  };

  const getHeaders = () => ({
    authorization: `Bearer ${user.token}`,
    "Content-Type": "application/json",
  });

  const fetchSettings = async (endpoint, selectedPropertyId = null) => {
    const propertyParam = getPropertyParam(endpoint, selectedPropertyId);
    const response = await axios.get(
      `${Config.apiUrl}${endpoint}${propertyParam}`,
      { headers: getHeaders() }
    );
    return response.data;
  };
  // Management settings mutation
  const {
    mutate: fetchManagementSettingsMutate,
    isLoading: fetchManagementSettingsLoading,
  } = useMutation(() => fetchSettings("/property/web/management/details/"), {
    onSuccess: (res) => {
      if (res?.isSuccess) {
        setSettingsData(res?.data);
        updateModalState("managementModel", true);
      }
    },
  });

  // Rent settings mutation
  // const { mutate: fetchRentSettingsMutate } = useMutation(
  //   () => fetchSettings("/property/web/renting/rules/"),
  //   {
  //     onSuccess: (res) => {
  //       if (res?.isSuccess) {
  //         setSettingsData(res?.data);
  //         updateModalState("rentModel", true);
  //       }
  //     },
  //   }
  // );
  const {
    mutate: fetchRentSettingsMutate,
    isLoading: fetchRentSettingsLoading,
  } = useMutation(
    (selectedPropertyId) =>
      fetchSettings("/property/web/renting/rules/", selectedPropertyId),
    {
      onSuccess: (res) => {
        if (res?.isSuccess) {
          setSettingsData(res?.data);
          updateModalState("rentModel", true);
        }
      },
      onError: (error) => {
        toast.error("Error fetching property details");
      },
    }
  );
  // Notification settings mutation
  const {
    mutate: fetchNotiSettingsMutate,
    isLoading: fetchNotifySettingsLoading,
  } = useMutation(() => fetchSettings("/settings/list/"), {
    onSuccess: (res) => {
      if (res?.isSuccess) {
        setSettingsData(res?.data);
        updateModalState("notiModel", true);
      }
    },
  });

  // Onboarding settings mutation
  const {
    mutate: fetchOnboardingSettingsMutate,
    isLoading: fetchOnboardingSettingsLoading,
  } = useMutation(() => fetchSettings("/property/list/tenant/onboarding/"), {
    onSuccess: (res) => {
      if (res?.isSuccess) {
        setSettingsData(res?.data);
        updateModalState("onboardingModel", true);
      }
    },
  });

  // Update settings mutations - updated to handle property ID parameter
  const { mutate: editNotiSettingsMutate, isLoading: editNotiSettingsLoading } =
    useMutation(
      (values) => {
        const payload = {
          propId: values.selectedProperties.join(","),
          type: values.key,
          isEnabled: values.isEnabled ? 1 : 0,
        };
        return axios.post(`${Config.apiUrl}/settings/edit`, payload, {
          headers: getHeaders(),
        });
      },
      {
        onSuccess: (res) => {
          if (res?.data?.isSuccess) {
            toast.success(res?.data?.msg || "Settings updated successfully.");
          } else {
            toast.error(res?.data?.msg || "Failed to update settings.");
          }
        },
        onError: () => {
          toast.error("Error updating settings.");
        },
      }
    );

  const {
    mutate: editManagementDetailMutate,
    isLoading: editManagementDetailLoading,
  } = useMutation(
    (values) => {
      const propertyParam = getPropertyParam();
      const payload = {
        propId: propertyParam,
        name: values.name,
        email: values.email,
        mobile: values.mobile,
        businessName: values.businessName,
        gstNo: values.gstNo || "",
      };
      return axios.post(
        `${Config.apiUrl}/property/web/management/details/edit`,
        payload,
        { headers: getHeaders() }
      );
    },
    {
      onSuccess: (res) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.msg);
          updateModalState("managementModel", false);
          setOpenModal(null);
        }
      },
    }
  );

  const {
    mutate: editRentingDetailMutate,
    isLoading: editRentingDetailLoading,
  } = useMutation(
    (submitData) => {
      // const propertyParam = getPropertyParam();
      const selectedPropertyId = submitData?.selectedPropertyId;

      let propertyParam;
      if (submitData.replicate === 1) {
        propertyParam = getAllPropertyIds();
      } else if (selectedPropertyId) {
        propertyParam = selectedPropertyId;
      } else {
        propertyParam = getPropertyParam("/property/web/renting/rules/");
      }
      const payload = {
        propId: propertyParam,
        rent: submitData.rent,
        security: submitData.security,
        rentalCycle: submitData.rentalCycle,
        lockInPeriod: submitData.lockInPeriod,
        noticePeriod: submitData.noticePeriod,
        agreementPeriod: submitData.agreementDuration || "",
        replicate: submitData.replicate || 0,
      };
      return axios.post(
        `${Config.apiUrl}/property/renting/rules/edit`,
        payload,
        { headers: getHeaders() }
      );
    },
    {
      onSuccess: (res) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.msg);
          updateModalState("rentModel", false);
          setOpenModal(null);
        }
      },
    }
  );

  const {
    mutate: editOnboardSettingsMutate,
    isLoading: editOnboardSettingsLoading,
  } = useMutation(
    (values) => {
      // const propertyParam = getPropertyParam();
      const payload = {
        type: values.type,
        isEnabled: values.isEnabled ? 1 : 0,
        propId: values.selectedProperties.join(","),
      };
      return axios.post(
        `${Config.apiUrl}/property/update/tenant/onboarding`,
        payload,
        { headers: getHeaders() }
      );
    },
    {
      onSuccess: (res) => {
        if (res?.data?.isSuccess) {
          toast.success(res?.data?.msg || "Settings updated successfully.");
        } else {
          toast.error(res?.data?.msg || "Failed to update settings.");
        }
      },
      onError: () => {
        toast.error("Error updating settings.");
      },
    }
  );

  // Fetch data when modal changes
  useEffect(() => {
    if (!openModal) return;

    const modalActions = {
      ManagementDetails: fetchManagementSettingsMutate,
      RentingStayRules: fetchRentSettingsMutate,
      NotificationSettings: fetchNotiSettingsMutate,
      OnboardingSettings: fetchOnboardingSettingsMutate,
    };

    if (modalActions[openModal]) {
      modalActions[openModal]();
    }
  }, [openModal]);

  // Handle opening modals
  const handleOpenModal = (modal) => {
    // Reset all modal states
    Object.keys(modalStates).forEach((key) => updateModalState(key, false));
    setOpenModal(modal);
  };

  // Close all modals
  const handleCloseModal = () => {
    setOpenModal(null);
  };

  return (
    <>
      <BoxContainer>
        <BoxHead>
          <BoxTitle className="!mb-0 whitespace-nowrap">
            Settings
            <Underline />
          </BoxTitle>
        </BoxHead>

        <TableWrapper>
          <Table
            setOpenModal={handleOpenModal}
            settingsData={settingsData}
            setIsLogout={setIsLogout}
            property={property}
            propertyList={propertyList}
          />
        </TableWrapper>

        {/* Modal backdrop */}
        {/* <div
          className={`${
            openModal ? "flex" : "hidden"
          } fixed inset-0 h-screen w-screen bg-black/30 backdrop-blur-sm transition-all duration-800 ease-in-out z-10`}
          onClick={handleCloseModal}
        />

        <div
          className={`${
            openModal
              ? "translate-x-0 opacity-100"
              : "translate-x-full opacity-0"
          } fixed h-screen right-0 top-0 transition-all duration-800 ease-in-out bg-white flex items-start md:rounded-l-2xl overflow-hidden md:w-1/2 w-full lg:w-1/3 justify-start z-50`}
        > */}
        <SideModal isOpen={openModal} onClose={() => setOpenModal(false)}>
          {openModal === "ManagementDetails" && (
            <ManagementDetailsModal
              openModal={openModal}
              setOpenModal={setOpenModal}
              managementSettingsData={settingsData || {}}
              editManagementDetailMutate={editManagementDetailMutate}
              editManagementDetailLoading={editManagementDetailLoading}
              isOpen={openModal === "ManagementDetails"}
              onClose={() => {
                updateModalState("managementModel", false);
                setOpenModal(null);
              }}
              property={property}
              propertyList={propertyList}
              fetchManagementSettingsLoading={fetchManagementSettingsLoading}
              isLoading={fetchManagementSettingsLoading}
              isDataLoaded={modalStates.managementModel}
            />
          )}

          {/* Renting Stay Rules Modal */}
          {openModal === "RentingStayRules" && (
            <RentingStayRulesModal
              openModal={openModal}
              setOpenModal={setOpenModal}
              editRentingDetailMutate={editRentingDetailMutate}
              editRentingDetailLoading={editRentingDetailLoading}
              settingsData={settingsData || {}}
              isOpen={openModal === "RentingStayRules"}
              onClose={handleCloseModal}
              property={property}
              propertyList={propertyList}
              propertyParam={getPropertyParam("/property/web/renting/rules/")}
              rentingDetailsMutate={fetchRentSettingsMutate}
              isLoading={fetchManagementSettingsLoading}
              isDataLoaded={modalStates.rentModel}
            />
          )}

          {/* Eviction Settings Modal */}
          {openModal === "EvictionSettings" && (
            <EvictionSettingsModal
              isOpen={openModal === "EvictionSettings"}
              onClose={handleCloseModal}
              settingsData={settingsData?.evictionSettings}
              property={property}
              propertyList={propertyList}
            />
          )}

          {/* Notification Settings Modal */}
          {openModal === "NotificationSettings" && (
            <NotificationSettings
              isOpen={openModal === "NotificationSettings"}
              onClose={handleCloseModal}
              editNotiSettingsMutate={editNotiSettingsMutate}
              editNotiSettingsLoading={editNotiSettingsLoading}
              settingsData={settingsData || {}}
              property={property}
              propertyList={propertyList}
              fetchNotifySettingsLoading={fetchNotifySettingsLoading}
              isDataLoaded={modalStates.notiModel}
            />
          )}

          {/* Onboarding Settings Modal */}
          {openModal === "OnboardingSettings" && (
            <OnboardingSettingsModal
              onClose={() => {
                updateModalState("onboardingModel", false);
                setOpenModal(null);
              }}
              settingsData={settingsData || {}}
              isonboardSettingsLoading={editOnboardSettingsLoading}
              editOnboardSettingsMutate={editOnboardSettingsMutate}
              property={property}
              propertyList={propertyList}
              fetchOnboardingSettingsLoading={fetchOnboardingSettingsLoading}
              isDataLoaded={modalStates.onboardingModel}
            />
          )}
        </SideModal>
        {/* </div> */}

        {/* Logout Modal */}
        {isLogout && <Logout isLogout={isLogout} setIsLogout={setIsLogout} />}
      </BoxContainer>
    </>
  );
};

export default Settings;




// import { useState, useEffect } from "react";
// import tw from "tailwind-styled-components";
// import { useSelector } from "react-redux";
// import { useMutation } from "react-query";
// import axios from "axios";
// import { toast } from "react-toastify";
// import Table from "../../Components/Settings_onprogress/Table";
// import {
//   BoxHead,
//   BoxContainer,
//   BoxTitle,
//   Underline,
// } from "../../Components/Styles/PageStyles";
// import RentingStayRulesModal from "../../Components/Settings_onprogress/RentingStayRulesModal";
// import ManagementDetailsModal from "../../Components/Settings_onprogress/ManagementDetailsModal";
// import NotificationSettings from "../../Components/Settings_onprogress/NotificationSettings";
// import OnboardingSettingsModal from "../../Components/Settings_onprogress/OnboardingSettings";
// import EvictionSettingsModal from "../../Components/Settings_onprogress/EvictionSettingsModal";
// import Logout from "../../Components/Logout";
// import Config from "../../Config";
// import SideModal from "../../Components/SideModal";
// import RenewAgreementRulesModal from "../../Components/Settings_onprogress/RenewAgreementRulesModal";
// import DuesSettingsModal from "../../Components/Settings_onprogress/DuesSettingsModal";

// const TableWrapper = tw.h1`my-10 rounded-md`;

// const Settings = () => {
//   const user = useSelector((state) => state.UserReducer.user);
//   const property = useSelector(
//     (state) => state.SelectPropertyReducer.selectProperty
//   );
//   const propertyList = useSelector((state) => state.PropertyReducer.property);
//   const [openModal, setOpenModal] = useState(null);
//   const [isLogout, setIsLogout] = useState(false);
//   const [settingsData, setSettingsData] = useState(null);
//   const [modalStates, setModalStates] = useState({
//     managementModel: false,
//     rentModel: false,
//     notiModel: false,
//     onboardingModel: false,
//     evictionModel: false,
//     duesModel: false,
//   });

//   const getAllPropertyIds = () => {
//     if (!propertyList || propertyList.length === 0) return "";

//     const validProperties = propertyList.filter((prop) => prop.id !== 0);
//     return validProperties.map((prop) => prop.id).join(",");
//   };

//   const getPropertyParam = (endpoint = "", selectedPropertyId = null) => {
//     if (selectedPropertyId) {
//       return selectedPropertyId;
//     }
//     if (property?.id === 0 && endpoint === "/property/web/renting/rules/") {
//       return propertyList[1]?.id || "";
//     }
//     if (property?.id === 0) {
//       return getAllPropertyIds();
//     }
//     return property?.id;
//   };

//   const updateModalState = (modalKey, value) => {
//     setModalStates((prev) => ({
//       ...prev,
//       [modalKey]: value,
//     }));
//   };

//   const getHeaders = () => ({
//     authorization: `Bearer ${user.token}`,
//     "Content-Type": "application/json",
//   });

//   const fetchSettings = async (endpoint, selectedPropertyId = null) => {
//     const propertyParam = getPropertyParam(endpoint, selectedPropertyId);
//     const response = await axios.get(
//       `${Config.apiUrl}${endpoint}${propertyParam}`,
//       { headers: getHeaders() }
//     );
//     return response.data;
//   };

//   // Management settings mutation
//   const {
//     mutate: fetchManagementSettingsMutate,
//     isLoading: fetchManagementSettingsLoading,
//   } = useMutation(() => fetchSettings("/property/web/management/details/"), {
//     onSuccess: (res) => {
//       if (res?.isSuccess) {
//         setSettingsData(res?.data);
//         updateModalState("managementModel", true);
//       }
//     },
//   });

//   // Rent settings mutation
//   const {
//     mutate: fetchRentSettingsMutate,
//     isLoading: fetchRentSettingsLoading,
//   } = useMutation(
//     (selectedPropertyId) =>
//       fetchSettings("/property/web/renting/rules/", selectedPropertyId),
//     {
//       onSuccess: (res) => {
//         if (res?.isSuccess) {
//           setSettingsData(res?.data);
//           updateModalState("rentModel", true);
//         }
//       },
//       onError: (error) => {
//         toast.error("Error fetching property details");
//       },
//     }
//   );

//   // Notification settings mutation
//   const {
//     mutate: fetchNotiSettingsMutate,
//     isLoading: fetchNotifySettingsLoading,
//   } = useMutation((selectedPropertyId) => fetchSettings("/settings/list/", selectedPropertyId), {
//     onSuccess: (res) => {
//       if (res?.isSuccess) {
//         setSettingsData(res?.data);
//         updateModalState("notiModel", true);
//       }
//     },
//   });

//   // Onboarding settings mutation - UPDATED to accept propertyId
//   const {
//     mutate: fetchOnboardingSettingsMutate,
//     isLoading: fetchOnboardingSettingsLoading,
//   } = useMutation(
//     (selectedPropertyId) =>
//       fetchSettings("/property/list/tenant/onboarding/", selectedPropertyId),
//     {
//       onSuccess: (res) => {
//         if (res?.isSuccess) {
//           setSettingsData(res?.data);
//           updateModalState("onboardingModel", true);
//         }
//       },
//     }
//   );

//   // Eviction settings mutation
//   const {
//     mutate: fetchEvictionSettingsMutate,
//     isLoading: fetchEvictionSettingsLoading,
//   } = useMutation(
//     (selectedPropertyId) =>
//       fetchSettings("/property/list/tenant/onboarding/", selectedPropertyId),
//     {
//       onSuccess: (res) => {
//         if (res?.isSuccess) {
//           setSettingsData(res?.data);
//           updateModalState("evictionModel", true);
//         }
//       },
//     }
//   );

//   // Dues settings mutation
//   const {
//     mutate: fetchDuesSettingsMutate,
//     isLoading: fetchDuesSettingsLoading,
//   } = useMutation(
//     (selectedPropertyId) =>
//       fetchSettings("/property/list/tenant/onboarding/", selectedPropertyId),
//     {
//       onSuccess: (res) => {
//         if (res?.isSuccess) {
//           setSettingsData(res?.data);
//           updateModalState("duesModel", true);
//         }
//       },
//     }
//   );

//   // Update notification settings mutation
//   const { mutate: editNotiSettingsMutate, isLoading: editNotiSettingsLoading } =
//     useMutation(
//       (values) => {
//         const payload = {
//           propId: values.selectedProperties.join(","),
//           type: values.key,
//           isEnabled: values.isEnabled ? 1 : 0,
//         };
//         return axios.post(`${Config.apiUrl}/settings/edit`, payload, {
//           headers: getHeaders(),
//         });
//       },
//       {
//         onSuccess: (res) => {
//           if (res?.data?.isSuccess) {
//             toast.success(res?.data?.msg || "Settings updated successfully.");
//           } else {
//             toast.error(res?.data?.msg || "Failed to update settings.");
//           }
//         },
//         onError: () => {
//           toast.error("Error updating settings.");
//         },
//       }
//     );

//   // Update management details mutation
//   const {
//     mutate: editManagementDetailMutate,
//     isLoading: editManagementDetailLoading,
//   } = useMutation(
//     (values) => {
//       const propertyParam = getPropertyParam();
//       const payload = {
//         propId: propertyParam,
//         name: values.name,
//         email: values.email,
//         mobile: values.mobile,
//         businessName: values.businessName,
//         gstNo: values.gstNo || "",
//       };
//       return axios.post(
//         `${Config.apiUrl}/property/web/management/details/edit`,
//         payload,
//         { headers: getHeaders() }
//       );
//     },
//     {
//       onSuccess: (res) => {
//         if (res?.data?.isSuccess) {
//           toast.success(res?.data?.msg);
//           updateModalState("managementModel", false);
//           setOpenModal(null);
//         }
//       },
//     }
//   );

//   // Update renting details mutation
//   const {
//     mutate: editRentingDetailMutate,
//     isLoading: editRentingDetailLoading,
//   } = useMutation(
//     (submitData) => {
//       const selectedPropertyId = submitData?.selectedPropertyId;

//       let propertyParam;
//       if (submitData.replicate === 1) {
//         propertyParam = getAllPropertyIds();
//       } else if (selectedPropertyId) {
//         propertyParam = selectedPropertyId;
//       } else {
//         propertyParam = getPropertyParam("/property/web/renting/rules/");
//       }
//       const payload = {
//         propId: propertyParam,
//         rent: submitData.rent,
//         security: submitData.security,
//         rentalCycle: submitData.rentalCycle,
//         lockInPeriod: submitData.lockInPeriod,
//         noticePeriod: submitData.noticePeriod,
//         agreementPeriod: submitData.agreementDuration || "",
//         replicate: submitData.replicate || 0,
//       };
//       return axios.post(
//         `${Config.apiUrl}/property/renting/rules/edit`,
//         payload,
//         { headers: getHeaders() }
//       );
//     },
//     {
//       onSuccess: (res) => {
//         if (res?.data?.isSuccess) {
//           toast.success(res?.data?.msg);
//           updateModalState("rentModel", false);
//           setOpenModal(null);
//         }
//       },
//     }
//   );


//   const {
//     mutate: editOnboardSettingsMutate,
//     isLoading: editOnboardSettingsLoading,
//   } = useMutation(
//     (values) => {
//       const payload = {
//         type: values.type,
//         isEnabled: values.isEnabled ? 1 : 0,
//         propId: values.selectedProperties.join(","),
//         replicate: values.replicate || 0,
//       };
//       return axios.post(
//         `${Config.apiUrl}/property/update/tenant/onboarding`,
//         payload,
//         { headers: getHeaders() }
//       );
//     },
//     {
//       onSuccess: (res) => {
//         if (res?.data?.isSuccess) {
//           toast.success(res?.data?.msg || "Settings updated successfully.");
//         } else {
//           toast.error(res?.data?.msg || "Failed to update settings.");
//         }
//       },
//       onError: () => {
//         toast.error("Error updating settings.");
//       },
//     }
//   );

//   // Update eviction settings mutation
//   const {
//     mutate: editEvictionSettingsMutate,
//     isLoading: editEvictionSettingsLoading,
//   } = useMutation(
//     (values) => {
//       const payload = {
//         type: values.type,
//         isEnabled: values.isEnabled ? 1 : 0,
//         propId: values.selectedProperties.join(","),
//       };
//       return axios.post(
//         `${Config.apiUrl}/property/update/tenant/eviction`,
//         payload,
//         { headers: getHeaders() }
//       );
//     },
//     {
//       onSuccess: (res) => {
//         if (res?.data?.isSuccess) {
//           toast.success(res?.data?.msg || "Eviction settings updated successfully.");
//         } else {
//           toast.error(res?.data?.msg || "Failed to update eviction settings.");
//         }
//       },
//       onError: () => {
//         toast.error("Error updating eviction settings.");
//       },
//     }
//   );

//   // Update dues settings mutation
//   const {
//     mutate: editDuesSettingsMutate,
//     isLoading: editDuesSettingsLoading,
//   } = useMutation(
//     (values) => {
//       const payload = {
//         type: values.type,
//         isEnabled: values.isEnabled ? 1 : 0,
//         propId: values.selectedProperties.join(","),
//       };
//       return axios.post(
//         `${Config.apiUrl}/property/update/tenant/dues`,
//         payload,
//         { headers: getHeaders() }
//       );
//     },
//     {
//       onSuccess: (res) => {
//         if (res?.data?.isSuccess) {
//           toast.success(res?.data?.msg || "Dues settings updated successfully.");
//         } else {
//           toast.error(res?.data?.msg || "Failed to update dues settings.");
//         }
//       },
//       onError: () => {
//         toast.error("Error updating dues settings.");
//       },
//     }
//   );

//   useEffect(() => {
//     if (!openModal) return;

//     const modalActions = {
//       ManagementDetails: fetchManagementSettingsMutate,
//       RentingStayRules: fetchRentSettingsMutate,
//       NotificationSettings: () => {
//         if (property?.id !== 0) {
//           fetchNotiSettingsMutate(property?.id);
//         }
//       },
//       // NotificationSettings: fetchNotiSettingsMutate,
//       OnboardingSettings: () => {
//         if (property?.id !== 0) {
//           fetchOnboardingSettingsMutate(property?.id);
//         }
//       },
//       EvictionSettings: () => {
//         if (property?.id !== 0) {
//           fetchEvictionSettingsMutate(property?.id);
//         }
//       },
//       RenewAgreement: fetchRentSettingsMutate,
//       DuesSettings: () => {
//         if (property?.id !== 0) {
//           fetchDuesSettingsMutate(property?.id);
//         }
//       }
//     };

//     if (modalActions[openModal]) {
//       modalActions[openModal]();
//     }
//   }, [openModal]);

//   const handleOpenModal = (modal) => {
//     Object.keys(modalStates).forEach((key) => updateModalState(key, false));
//     setOpenModal(modal);
//   };

//   const handleCloseModal = () => {
//     setOpenModal(null);
//   };

//   return (
//     <>
//       <BoxContainer className="!h-auto">
//         <BoxHead>
//           <BoxTitle className="!mb-0 whitespace-nowrap">
//             Settings
//             <Underline />
//           </BoxTitle>
//         </BoxHead>

//         <TableWrapper>
//           <Table
//             setOpenModal={handleOpenModal}
//             settingsData={settingsData}
//             setIsLogout={setIsLogout}
//             property={property}
//             propertyList={propertyList}
//           />
//         </TableWrapper>

//         <SideModal isOpen={openModal} onClose={() => setOpenModal(false)}>
//           {openModal === "ManagementDetails" && (
//             <ManagementDetailsModal
//               openModal={openModal}
//               setOpenModal={setOpenModal}
//               managementSettingsData={settingsData || {}}
//               editManagementDetailMutate={editManagementDetailMutate}
//               editManagementDetailLoading={editManagementDetailLoading}
//               isOpen={openModal === "ManagementDetails"}
//               onClose={() => {
//                 updateModalState("managementModel", false);
//                 setOpenModal(null);
//               }}
//               property={property}
//               propertyList={propertyList}
//               fetchManagementSettingsLoading={fetchManagementSettingsLoading}
//               isLoading={fetchManagementSettingsLoading}
//               isDataLoaded={modalStates.managementModel}
//             />
//           )}

//           {/* Renting Stay Rules Modal */}
//           {openModal === "RentingStayRules" && (
//             <RentingStayRulesModal
//               openModal={openModal}
//               setOpenModal={setOpenModal}
//               editRentingDetailMutate={editRentingDetailMutate}
//               editRentingDetailLoading={editRentingDetailLoading}
//               settingsData={settingsData || {}}
//               isOpen={openModal === "RentingStayRules"}
//               onClose={handleCloseModal}
//               property={property}
//               propertyList={propertyList}
//               propertyParam={getPropertyParam("/property/web/renting/rules/")}
//               rentingDetailsMutate={fetchRentSettingsMutate}
//               isLoading={fetchRentSettingsLoading}
//               isDataLoaded={modalStates.rentModel}
//             />
//           )}

//           {/* Renew Agreement Modal */}
//           {openModal === "RenewAgreement" && (
//             <RenewAgreementRulesModal
//               openModal={openModal}
//               setOpenModal={setOpenModal}
//               editRentingDetailMutate={editRentingDetailMutate}
//               editRentingDetailLoading={editRentingDetailLoading}
//               settingsData={settingsData || {}}
//               isOpen={openModal === "RenewAgreement"}
//               onClose={handleCloseModal}
//               property={property}
//               propertyList={propertyList}
//               propertyParam={getPropertyParam("/property/web/renting/rules/")}
//               rentingDetailsMutate={fetchRentSettingsMutate}
//               isLoading={fetchRentSettingsLoading}
//               isDataLoaded={modalStates.rentModel}
//             />
//           )}

//           {/* Eviction Settings Modal */}
//           {openModal === "EvictionSettings" && (
//             <EvictionSettingsModal
//               isOpen={openModal === "EvictionSettings"}
//               onClose={handleCloseModal}
//               settingsData={settingsData || {}}
//               isLoading={editEvictionSettingsLoading}
//               editSettingsMutate={editEvictionSettingsMutate}
//               property={property}
//               propertyList={propertyList}
//               fetchSettingsLoading={fetchEvictionSettingsLoading}
//               isDataLoaded={modalStates.evictionModel}
//               fetchEvictionSettingsMutate={fetchEvictionSettingsMutate}
//               propertyParam={property?.id !== 0 ? property?.id : null}
//             />
//           )}

//           {/* Notification Settings Modal */}
//           {openModal === "NotificationSettings" && (
//             <NotificationSettings
//               isOpen={openModal === "NotificationSettings"}
//               onClose={handleCloseModal}
//               editNotiSettingsMutate={editNotiSettingsMutate}
//               editNotiSettingsLoading={editNotiSettingsLoading}
//               settingsData={settingsData || {}}
//               property={property}
//               propertyList={propertyList}
//               fetchNotifySettingsLoading={fetchNotifySettingsLoading}
//               isDataLoaded={modalStates.notiModel}
//               fetchNotiSettingsMutate={fetchNotiSettingsMutate}
//               propertyParam={property?.id !== 0 ? property?.id : null}
//             />
//           )}

//           {/* Onboarding Settings Modal */}
//           {openModal === "OnboardingSettings" && (
//             <OnboardingSettingsModal
//               onClose={() => {
//                 updateModalState("onboardingModel", false);
//                 setOpenModal(null);
//               }}
//               settingsData={settingsData || {}}
//               isLoading={editOnboardSettingsLoading}
//               editSettingsMutate={editOnboardSettingsMutate}
//               property={property}
//               propertyList={propertyList}
//               fetchSettingsLoading={fetchOnboardingSettingsLoading}
//               isDataLoaded={modalStates.onboardingModel}
//               fetchOnboardingSettingsMutate={fetchOnboardingSettingsMutate}
//               propertyParam={property?.id !== 0 ? property?.id : null}
//             />
//           )}

//           {/* Dues Settings Modal */}
//           {openModal === "DuesSettings" && (
//             <DuesSettingsModal
//               isOpen={openModal === "DuesSettings"}
//               onClose={handleCloseModal}
//               settingsData={settingsData || {}}
//               isLoading={editDuesSettingsLoading}
//               editSettingsMutate={editDuesSettingsMutate}
//               property={property}
//               propertyList={propertyList}
//               fetchSettingsLoading={fetchDuesSettingsLoading}
//               isDataLoaded={modalStates.duesModel}
//               fetchDuesSettingsMutate={fetchDuesSettingsMutate}
//               propertyParam={property?.id !== 0 ? property?.id : null}
//             />
//           )}
//         </SideModal>

//         {/* Logout Modal */}
//         {isLogout && <Logout isLogout={isLogout} setIsLogout={setIsLogout} />}
//       </BoxContainer>
//     </>
//   );
// };

// export default Settings;